import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { DeckFrame } from '@/components/builder/DeckFrame';

interface DeckFullscreenProps {
  html: string;
  /** Number of visible slides — the deck document shows only those. */
  slideCount: number;
  initialIndex?: number;
  onExit: () => void;
}

/** Keys that move the deck. Both the host and the deck document understand them. */
const FORWARD_KEYS = ['ArrowRight', 'ArrowDown', 'PageDown', ' ', 'Spacebar'];
const BACK_KEYS = ['ArrowLeft', 'ArrowUp', 'PageUp'];

/**
 * Presentation view.
 *
 * Keys are handled twice on purpose. The deck document has its own handler — it is the
 * same script that runs on a shared link, where there is no app around it — but it only
 * ever sees keys pressed while the iframe holds focus. Someone who just clicked the
 * "full screen" button is still focused on the app, so the host listens too, and the two
 * stay in step through the postMessage bridge in DeckFrame.
 *
 * The real browser full screen is requested rather than faked with a fixed overlay: a
 * pitch shown from a laptop should not have a browser chrome and a taskbar around it.
 * If the request is refused (older Safari, an iframe without the permission) the overlay
 * still covers the viewport — the view works, it just is not full screen.
 */
export function DeckFullscreen({ html, slideCount, initialIndex = 0, onExit }: DeckFullscreenProps) {
  const { t } = useTranslation('builder');
  const containerRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(initialIndex);

  const exit = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
    onExit();
  }, [onExit]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node?.requestFullscreen) return;
    // Refused when the gesture is not recognised as a user action; the overlay still works.
    void node.requestFullscreen().catch(() => {});
  }, []);

  // Leaving full screen by the browser's own means (Esc, F11, the system button) has to
  // close the view too, or the app is left with an overlay nobody asked for.
  useEffect(() => {
    function onFullscreenChange() {
      if (!document.fullscreenElement) onExit();
    }
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, [onExit]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (FORWARD_KEYS.includes(event.key)) {
        event.preventDefault();
        setIndex((current) => Math.min(slideCount - 1, current + 1));
      } else if (BACK_KEYS.includes(event.key)) {
        event.preventDefault();
        setIndex((current) => Math.max(0, current - 1));
      } else if (event.key === 'Home') {
        event.preventDefault();
        setIndex(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        setIndex(Math.max(0, slideCount - 1));
      } else if (event.key === 'Escape') {
        // Without full screen the browser sends nothing else, so close it here.
        if (!document.fullscreenElement) exit();
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [slideCount, exit]);

  const progress = slideCount > 1 ? ((index + 1) / slideCount) * 100 : 100;

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-label={t('deck.view.title')}
      className="fixed inset-0 z-50 flex flex-col bg-black"
    >
      <div className="flex items-center gap-[12px] px-[16px] py-[10px]">
        <span className="font-segoe text-[13px] text-white/70">{t('deck.view.hint')}</span>
        <span className="ml-auto font-manrope text-[13px] font-semibold text-white/90">
          {index + 1} / {Math.max(slideCount, 1)}
        </span>
        <Button variant="brand-outline" size="brand-sm" onClick={exit} className="border-white/40 text-white">
          {t('deck.view.exit')}
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center px-[16px] pb-[16px]">
        <DeckFrame html={html} slideIndex={index} onSlideChange={setIndex} className="max-w-full" />
      </div>

      <div className="h-[3px] w-full bg-white/15">
        <div
          className="h-full bg-[var(--main-brand,#2197f1)] transition-[width] duration-200"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
